import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";

import { authService } from "@/services/authService";
import { hasBadge } from "@/composables/useAuth";
import {
  buildDestinationFromRoute,
  getStoredReferralCode,
  recordReferralVisit,
  storeReferralCode,
} from "@/utils/referral";
import { isMusicModeEnabledAsync, useAppSettings } from "@/composables/useAppSettings";


// Lazy load views
const GenresView = () => import("@/views/GenresView.vue");
const GenrePickerTestPage = () => import("@/views/GenrePickerTestPage.vue");
const PlaylistsView = () => import("@/views/PlaylistsView.vue");
const PlaylistDetailView = () => import("@/views/PlaylistDetailView.vue");
const LikedSongsView = () => import("@/views/LikedSongsView.vue");
const RateSongsView = () => import("@/views/RateSongsView.vue");
const SongAnalyticsView = () => import("@/views/SongAnalyticsView.vue");
const LoginView = () => import("@/views/LoginView.vue");
const ExploreView = () => import("@/views/ExploreView.vue");
const TimelineView = () => import("@/views/TimelineView.vue");
const ListenView = () => import("@/views/ListenView.vue");
const TrendingView = () => import("@/views/TrendingView.vue");
const SearchView = () => import("@/views/SearchView.vue");
const NotificationsView = () => import("@/views/NotificationsView.vue");
const BookmarksView = () => import("@/views/BookmarksView.vue");
const MessagesView = () => import("@/views/MessagesView.vue");
const SettingsView = () => import("@/views/SettingsView.vue");
const FeedbackView = () => import("@/views/FeedbackView.vue");
const ManagedUsersView = () => import("@/views/ManagedUsersView.vue");
const GroupsView = () => import("@/views/GroupsView.vue");
const GroupDetailView = () => import("@/views/GroupDetailView.vue");
const GroupManagementView = () => import("@/views/GroupManagementView.vue");
const ProfileView = () => import("@/views/ProfileView.vue");
const AccountSetupView = () => import("@/views/AccountSetupView.vue");

// Detail views
const PostDetailView = () => import("@/views/PostDetailView.vue");
const SongDetailView = () => import("@/views/SongDetailView.vue");
const VideoDetailView = () => import("@/views/VideoDetailView.vue");

// User views
const BadgeCollectionView = () => import("@/views/BadgeCollectionView.vue");

// Admin views
const CEODashboardView = () => import("@/views/CEODashboardView.vue");
const ModerationFeedView = () => import("@/views/ModerationFeedView.vue");

// PWA views
const ShareView = () => import("@/views/ShareView.vue");
const PwaTestView = () => import("@/views/PwaTestView.vue");

// Help views
const HelpSupportView = () => import("@/views/HelpSupportView.vue");

// OAuth views
const OAuthCallbackView = () => import("@/views/OAuthCallbackView.vue");

// WSOM views
const WsomView = () => import("@/views/WsomView.vue");
const WsomResultsView = () => import("@/views/WsomResultsView.vue");
const WsomEventsView = () => import("@/views/WsomEventsView.vue");
const WsomEventDetailView = () => import("@/views/WsomEventDetailView.vue");
const WsomLeaderboardsView = () => import("@/views/WsomLeaderboardsView.vue");
const WsomEventVotingView = () => import("@/views/WsomEventVotingView.vue");

// Smart Playlists views
const SmartPlaylistsView = () => import("@/views/SmartPlaylistsView.vue");

// Radio Stations views
const RadioStationsView = () => import("@/views/RadioStationsView.vue");



const routes: RouteRecordRaw[] = [
  // ============================================================
  // HOME ROUTE - Redirects based on site mode (handled in beforeEach guard)
  // Music mode enabled -> /explore (music-focused hub)
  // Music mode disabled (social only) -> /timeline (social feed)
  // ============================================================
  {
    path: "/",
    name: "home",
    // No component - redirect handled dynamically in beforeEach guard
    component: { render: () => null },
  },

  // ============================================================
  // MAIN ROUTES - Combined music/social experience
  // ============================================================
  {
    path: "/explore",
    name: "explore",
    component: ExploreView,
  },
  {
    path: "/listen/:tab?",
    name: "listen",
    component: ListenView,
  },
  {
    path: "/radio-stations",
    name: "radio-stations",
    component: RadioStationsView,
  },
  {
    path: "/discover",
    redirect: "/discover/songs",
  },
  {
    path: "/discover/:category",
    name: "discover",
    component: TimelineView,
  },
  {
    path: "/trending",
    name: "trending",
    component: TrendingView,
  },
  {
    path: "/timeline/:scope(all|mystuff)?",
    name: "timeline",
    component: TimelineView,
  },
  {
    // Legacy redirect for old /discover/timeline URLs
    path: "/discover/timeline/:scope(all|mystuff)?",
    redirect: (to) => ({
      name: "timeline",
      params: { scope: to.params.scope || "all" },
      query: to.query,
    }),
  },

  // ============================================================
  // MUSIC ROUTES - Music-specific features
  // ============================================================
  {
    path: "/genres",
    name: "genres",
    component: GenresView,
    meta: { requiresAuth: true },
  },
  {
    path: "/playlists",
    name: "playlists",
    component: PlaylistsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/playlists/:ulid/:slug?",
    redirect: (to) => ({
      name: "playlist-detail",
      params: { ulid: to.params.ulid, slug: to.params.slug },
      query: to.query,
    }),
  },
  {
    path: "/playlists/discover",
    name: "playlists-discover",
    component: PlaylistsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/playlist/:ulid/:slug?",
    name: "playlist-detail",
    component: PlaylistDetailView,
  },
  {
    path: "/liked",
    name: "liked-songs",
    component: LikedSongsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/rate",
    name: "rate-songs",
    component: RateSongsView,
  },
  {
    path: "/wsom",
    name: "wsom",
    component: WsomView,
  },
  {
    path: "/wsom/results/:contestUlid?",
    name: "wsom-results",
    component: WsomResultsView,
  },
  {
    path: "/wsom/events",
    name: "wsom-events",
    component: WsomEventsView,
    meta: { requiresAuth: true, requiresBetaTester: true },
  },
  {
    path: "/wsom/events/:eventId",
    name: "wsom-event-detail",
    component: WsomEventDetailView,
    meta: { requiresAuth: true, requiresBetaTester: true },
  },
  {
    path: "/wsom/events/:eventId/vote",
    name: "wsom-event-voting",
    component: WsomEventVotingView,
    meta: { requiresAuth: true },
  },
  {
    path: "/wsom/leaderboards",
    name: "wsom-leaderboards",
    component: WsomLeaderboardsView,
    meta: { requiresAuth: true, requiresBetaTester: true },
  },
  {
    path: "/made-for-you",
    name: "smart-playlists",
    component: SmartPlaylistsView,
  },

  // ============================================================
  // LEGACY REDIRECTS - Support old /music/ and /social/ prefixed routes
  // ============================================================
  // Music legacy routes
  { path: "/music", redirect: "/explore" },
  { path: "/music/all", redirect: "/explore" },
  { path: "/music/explore", redirect: "/explore" },
  { path: "/music/explore/trending", redirect: "/discover/trending" },
  { path: "/music/explore/:category", redirect: (to) => `/discover/${to.params.category}` },
  { path: "/music/discover", redirect: "/discover/trending" },
  { path: "/music/discover/:category", redirect: (to) => `/discover/${to.params.category}` },
  { path: "/music/trending", redirect: "/trending" },
  { path: "/music/new", redirect: "/discover/new" },
  { path: "/music/new/:genre", redirect: "/discover/new" },
  { path: "/music/genres", redirect: "/genres" },
  { path: "/music/playlists", redirect: "/playlists" },
  { path: "/music/playlist/:id/:slug?", redirect: (to) => `/playlist/${to.params.id}${to.params.slug ? "/" + to.params.slug : ""}` },
  { path: "/music/liked", redirect: "/liked" },
  { path: "/music/rate", redirect: "/rate" },
  { path: "/music/wsom", redirect: "/wsom" },
  { path: "/music/wsom/results/:contestUlid?", redirect: (to) => `/wsom/results/${to.params.contestUlid || ""}` },
  { path: "/music/made-for-you", redirect: "/made-for-you" },

  // Social legacy routes
  { path: "/social", redirect: "/explore" },
  { path: "/social/all", redirect: "/explore" },
  { path: "/social/explore", redirect: "/explore" },
  { path: "/social/discover", redirect: "/discover/for-you" },
  { path: "/social/discover/:category", redirect: (to) => `/discover/${to.params.category}` },
  { path: "/social/trending", redirect: "/trending" },
  { path: "/social/forYou", redirect: "/discover/for-you" },
  { path: "/social/following", redirect: "/discover/following" },
  { path: "/social/creators", redirect: "/discover/creators" },
  { path: "/social/groups", redirect: "/groups" },
  { path: "/social/groups/:id", redirect: (to) => `/groups/${to.params.id}` },
  { path: "/social/groups/:id/manage", redirect: (to) => `/groups/${to.params.id}/manage` },
  { path: "/social/newUsers", redirect: "/discover/new-users" },

  // Old route redirects
  { path: "/popular", redirect: "/discover/popular" },
  { path: "/popular/:genre", redirect: "/discover/popular" },
  { path: "/liked-songs", redirect: "/liked" },
  { path: "/rate-songs", redirect: "/rate" },
  { path: "/analytics", redirect: (to) => {
    const user = authService.getUser();
    return user?.username ? `/profile/${user.username}/analytics/overview` : "/";
  }},

  // ============================================================
  // GROUP ROUTES
  // ============================================================
  {
    path: "/groups",
    name: "groups",
    component: GroupsView,
  },
  {
    path: "/groups/:id",
    name: "group-detail",
    component: GroupDetailView,
  },
  {
    path: "/groups/:id/manage",
    name: "group-manage",
    component: GroupManagementView,
    meta: { requiresAuth: true },
  },

  // ============================================================
  // TEST/DEMO ROUTES
  // ============================================================
  // (Injected below in dev only)

  // ============================================================
  // UTILITY ROUTES
  // ============================================================
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { requiresGuest: true },
  },
  {
    path: "/auth/:provider/callback",
    name: "oauth-callback",
    component: OAuthCallbackView,
  },
  {
    path: "/setup",
    name: "setup",
    component: AccountSetupView,
    meta: { requiresAuth: true },
  },
  {
    path: "/search",
    name: "search",
    component: SearchView,
  },
  {
    path: "/share",
    name: "share",
    component: ShareView,
  },
  {
    path: "/pwa-test",
    name: "pwa-test",
    component: PwaTestView,
  },
  {
    path: "/notifications",
    name: "notifications",
    component: NotificationsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/bookmarks",
    name: "bookmarks",
    component: BookmarksView,
    meta: { requiresAuth: true },
  },
  {
    path: "/messages",
    name: "messages",
    component: MessagesView,
    meta: { requiresAuth: true, requiresFeature: 'messaging' },
  },
  {
    path: "/messages/:ulid",
    name: "conversation",
    component: MessagesView,
    meta: { requiresAuth: true, requiresFeature: 'messaging' },
  },
  {
    path: "/settings",
    name: "settings",
    component: SettingsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/settings/feedback",
    name: "feedback",
    component: FeedbackView,
    meta: { requiresAuth: true },
  },
  {
    path: "/help",
    name: "help",
    component: HelpSupportView,
  },
  {
    path: "/manage-users",
    name: "manage-users",
    component: ManagedUsersView,
    meta: { requiresAuth: true },
  },
  {
    path: "/badges",
    name: "badges",
    component: BadgeCollectionView,
    meta: { requiresAuth: true },
  },
  {
    path: "/admin/dashboard",
    name: "ceo-dashboard",
    component: CEODashboardView,
    meta: { requiresAuth: true },
    beforeEnter: (_to, _from, next) => {
      const user = authService.getUser();
      const hasAdministratorBadge = user?.badges?.includes('administrator') ?? false;

      if (!hasAdministratorBadge) {
        next({ path: '/' });
        return;
      }

      next();
    },
  },
  {
    path: "/moderation",
    name: "moderation",
    component: ModerationFeedView,
    meta: { requiresAuth: true },
    beforeEnter: (_to, _from, next) => {
      const user = authService.getUser();
      // Allow access if user has administrator badge, moderator badge, or moderator role
      const hasAdministratorBadge = user?.badges?.includes('administrator') ?? false;
      const hasModeratorBadge = user?.badges?.includes('moderator') ?? false;
      const hasModeratorRole = user?.roles?.includes('moderator') ?? false;
      const hasSiteAdminRole = user?.roles?.includes('site-admin') ?? false;

      if (!hasAdministratorBadge && !hasModeratorBadge && !hasModeratorRole && !hasSiteAdminRole) {
        next({ path: '/' });
        return;
      }

      next();
    },
  },


  // ============================================================
  // PROFILE ROUTES
  // ============================================================
  {
    path: "/profile/:username/settings",
    name: "profile-settings",
    component: SettingsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile/:username/:tab?",
    name: "profile",
    component: ProfileView,
    beforeEnter: (to, _from, next) => {
      // Default to songs tab if no tab specified
      if (!to.params.tab) {
        next({
          name: 'profile',
          params: { username: to.params.username, tab: 'songs' },
          replace: false,
        });
      } else {
        next();
      }
    },
  },

  // ============================================================
  // ANALYTICS ROUTES (Profile-based)
  // ============================================================
  {
    path: "/profile/:username/analytics",
    redirect: (to) => `/profile/${to.params.username}/analytics/overview`,
  },
  {
    path: "/profile/:username/analytics/overview",
    name: "analytics-overview",
    component: SongAnalyticsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile/:username/analytics/top-songs",
    name: "analytics-top-songs",
    component: SongAnalyticsView,
    meta: { requiresAuth: true },
  },
  {
    path: "/profile/:username/analytics/performance",
    name: "analytics-performance",
    component: SongAnalyticsView,
    meta: { requiresAuth: true },
  },

  // Legacy analytics redirect
  {
    path: "/music/analytics",
    redirect: () => {
      const user = authService.getUser();
      if (user?.username) {
        return `/profile/${user.username}/analytics/overview`;
      }
      return "/";
    },
  },

  // ============================================================
  // CONTENT DETAIL ROUTES
  // ============================================================
  {
    path: "/post/:ulid",
    name: "post-detail",
    component: PostDetailView,
  },
  {
    path: "/song/:ulid",
    name: "song-detail",
    component: SongDetailView,
  },
  {
    path: "/track/:ulid",
    name: "track-detail",
    redirect: (to) => ({
      name: "song-detail",
      params: { ulid: to.params.ulid },
    }),
  },
  {
    path: "/v/:ulid",
    name: "video-detail",
    component: VideoDetailView,
  },
  {
    path: "/s/:ulid",
    name: "short-detail",
    component: VideoDetailView,
  },
  {
    path: "/podcast/:ulid",
    name: "podcast-detail",
    component: SongDetailView,
  },
  {
    path: "/burst/:ulid",
    name: "burst-detail",
    component: PostDetailView,
  },

  // ============================================================
  // CATCH-ALL
  // ============================================================
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

// Keep test/demo views out of production bundles (especially important for iOS Safari perf).
// This also avoids shipping large mock-data screens and dev-only utilities.
if (import.meta.env.DEV) {
  routes.push(
    {
      path: "/genre-picker-test",
      name: "genre-picker-test",
      component: GenrePickerTestPage,
    },
    {
      path: "/dev/tools",
      name: "developer-tools",
      component: () => import("@/views/DeveloperToolsView.vue"),
    },
    {
      path: "/test/theme",
      name: "theme-test",
      component: () => import("@/views/ThemeTestView.vue"),
    },
    {
      path: "/test/post/card",
      name: "postcard-test",
      component: () => import("@/views/PostCardTestView.vue"),
    },
    {
      path: "/test/playlist/card",
      name: "playlistcard-test",
      component: () => import("@/views/PlaylistCardTestView.vue"),
    },
    {
      path: "/test/audio-player",
      name: "audioplayer-test",
      component: () => import("@/views/AudioPlayerTestView.vue"),
    },
  );
}

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Return false to disable automatic scroll restoration
    // We handle scroll position manually via useVirtualFeed composable
    return false;
  },
});

// If a route's component chunk fails to load (common after deployments when the
// client is still running an older build), navigation can appear "broken" until
// the user hard refreshes. Catch these errors and force a full reload.
router.onError((error, to) => {
  if (typeof window === "undefined") return;

  const message = String((error as any)?.message ?? error);

  // Log ALL router errors for debugging navigation issues
  console.error("[Router] Navigation error:", { message, to: to.fullPath, error });

  const isChunkLoadError =
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError") ||
    message.includes("dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Load failed") ||
    message.includes("NetworkError") ||
    message.includes("fetch");

  if (!isChunkLoadError) return;

  const reloadGuardKey = "router_chunk_reload_guard";
  const lastReloadAt = window.sessionStorage.getItem(reloadGuardKey);
  const lastReloadMs = lastReloadAt ? Number.parseInt(lastReloadAt, 10) : 0;

  // Avoid reload loops; allow one retry per minute.
  if (!lastReloadMs || Number.isNaN(lastReloadMs) || Date.now() - lastReloadMs > 60_000) {
    window.sessionStorage.setItem(reloadGuardKey, Date.now().toString());
    window.location.assign(to.fullPath);
  }
});

// Navigation watchdog - detects "stuck" navigation where URL changes but view doesn't update
// This can happen when chunk loading fails silently or component errors break reactivity
let navigationWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
let lastCompletedPath: string | null = null;

router.afterEach((to) => {
  // Clear any existing watchdog
  if (navigationWatchdogTimer) {
    clearTimeout(navigationWatchdogTimer);
    navigationWatchdogTimer = null;
  }

  // Track successful navigation
  lastCompletedPath = to.fullPath;

  // Set a watchdog that checks if the view actually rendered
  // The DOM should update within 500ms of navigation completing
  navigationWatchdogTimer = setTimeout(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    // Check if the current URL matches what we expect
    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    if (currentPath !== lastCompletedPath) {
      // URL has changed since afterEach, navigation is still in progress
      return;
    }

    // Check if router-view has rendered content
    const routerViewContent = document.querySelector(".app-layout__center");
    if (routerViewContent) {
      const hasContent = routerViewContent.children.length > 0;
      const isTransitioning = routerViewContent.querySelector(".page-fade-enter-active, .page-fade-leave-active");

      if (!hasContent && !isTransitioning) {
        console.warn("[Router] Navigation watchdog: View may be stuck - no content rendered after navigation to:", to.fullPath);
        // Don't auto-reload, just log for now to gather data
      }
    }
  }, 1000);
});

// Disable browser's native scroll restoration
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// Navigation guard for auth
router.beforeEach(async (to, _from, next) => {
  // Handle home route redirect based on site mode
  // When music mode is disabled (social only), redirect to /timeline instead of /explore
  if (to.name === "home") {
    const isMusicMode = await isMusicModeEnabledAsync();
    const targetPath = isMusicMode ? "/explore" : "/timeline";
    next(targetPath);
    return;
  }

  const isAuthenticated = authService.isAuthenticated();
  const user = authService.getUser();
  const referralCode =
    typeof to.query.ref === "string" ? to.query.ref : getStoredReferralCode();
  const destination = buildDestinationFromRoute(to);

  if (referralCode) {
    storeReferralCode(referralCode);
    void recordReferralVisit({
      referralCode,
      destinationType: destination.destinationType,
      destinationId: destination.destinationId,
      landingPath: to.fullPath,
    });
  }

  const isReferralLanding = Boolean(destination.destinationType);
  
  // Content detail pages (songs, posts, videos) can be viewed without authentication
  // even with referral codes - we'll show a signup prompt modal instead
  const isContentDetailPage = [
    "song-detail",
    "post-detail",
    "track-detail",
    "video-detail",
    "short-detail",
    "podcast-detail",
    "burst-detail",
  ].includes(to.name as string);

  if (
    !isAuthenticated &&
    referralCode &&
    isReferralLanding &&
    !isContentDetailPage &&
    to.name !== "login"
  ) {
    next({
      name: "login",
      query: { redirect: to.fullPath, ref: referralCode },
    });
    return;
  }

  // Routes that require authentication
  if (to.meta.requiresAuth && !isAuthenticated) {
    next({
      name: "login",
      query: { redirect: to.fullPath, ...(referralCode ? { ref: referralCode } : {}) },
    });
    return;
  }

  // Routes that require Beta Tester badge
  if (to.meta.requiresBetaTester && isAuthenticated && user) {
    if (!hasBadge(user.badges, 'Beta Tester')) {
      next("/");
      return;
    }
  }

  // Routes that require messaging tenant feature to be enabled
  if (to.meta.requiresFeature === 'messaging') {
    const { featureMessagingEnabled, loadSettings, isLoaded, isLoading } = useAppSettings();
    if (!isLoaded.value) {
      await loadSettings();
      // If loadSettings returned early due to concurrent load, wait for it to complete
      while (isLoading.value) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }
    if (!featureMessagingEnabled.value) {
      next('/');
      return;
    }
  }

  // Routes that require guest (login page)
  if (to.meta.requiresGuest && isAuthenticated) {
    next("/");
    return;
  }

  // Check if user needs to complete account setup
  if (
    isAuthenticated &&
    user &&
    user.accountSetup === false &&
    to.name !== "setup"
  ) {
    next({ name: "setup" });
    return;
  }

  // Redirect away from setup if already completed
  if (
    to.name === "setup" &&
    isAuthenticated &&
    user &&
    user.accountSetup !== false
  ) {
    next("/");
    return;
  }

  next();
});

export default router;
